import { Component, computed, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from './core/services/local-storage.service';
import { whiteLabelTarget } from './utils/whiteLabel';
import { Title } from '@angular/platform-browser';
import { DateAdapter } from '@angular/material/core';
import { CoreUserService } from './core/services/user.service';
import { NotificationService } from './core/services/notification.service';
import { SessionService } from './authentication/session.service';
import { Router } from '@angular/router';
import { AppStateService } from './store/app-state.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent {
  constructor(
    private translate: TranslateService,
    private localStorageService: LocalStorageService,
    private titleService: Title,
    private dateAdapter: DateAdapter<any>,
    private userService: CoreUserService,
    private notificationService: NotificationService,
    private sessionService: SessionService,
    private router: Router,
  ) {
    try {
      const email = this.localStorageService.get('email');
      const isPremiumLogin = this.localStorageService.get(
        'organic_signup_premium',
      );

      if (email == 'a.le@hein.eu' && isPremiumLogin) {
        localStorage.clear();
        this.router.navigate(['/login']);
      }
    } catch (e) {}

    const savedLang: string = this.localStorageService.get('language') || 'en';
    this.translate.setDefaultLang('en');
    this.translate.use(savedLang || 'en').subscribe();
    this.dateAdapter.setLocale(savedLang || 'en');

    this.setHtmlLang(savedLang || 'en');

    this.translate.onLangChange.subscribe((event) => {
      this.dateAdapter.setLocale(event.lang);
      this.setHtmlLang(event.lang);
    });

    const params = new URLSearchParams(window.location.search);
    params.forEach((value, key) => {
      if (key === 'auth_key') {
        this.localStorageService.set('auth_key', value);
      }
    });
  }

  private appState = inject(AppStateService);

  title = 'behamicsDashboard';

  showMainLoader: boolean = false;
  showContent: boolean = false;
  darkMode: boolean = false;

  whiteLabelTarget = whiteLabelTarget;

  isShopify = computed(() => this.appState.isShopify());
  showShopifyError: boolean = false;

  ngOnInit() {
    this.bridgeGoogleAdsOAuthCallback();
    if (this.whiteLabelTarget) {
      this.localStorageService.set('isWhiteLabel', true);

      try {
        const link = document.querySelector('link[rel="icon"]');
        if (link) {
          (link as HTMLLinkElement).href = this.whiteLabelTarget.icon.white;
        }
      } catch (e) {
        console.log(e);
      }

      this.titleService.setTitle(
        this.whiteLabelTarget.description + ' Dashboard',
      );
    }

    if (environment.production) {
      if (location.protocol === 'http:') {
        location.href = location.href.replace('http', 'https');
      }
    }

    const params = new URLSearchParams(location.search);
    params.forEach((value, key) => {
      if (key === 'seo_url') {
        this.localStorageService.set('seo_url_from_web', value);
      }
      if (key === 'gclid') {
        this.localStorageService.set('gclid_from_web', value);
      }
      if (key === 'utm_source') {
        this.localStorageService.set('utm_source_from_web', value);
      }
      if (key === 'utm_medium') {
        this.localStorageService.set('utm_medium_from_web', value);
      }
      if (key === 'utm_campaign') {
        this.localStorageService.set('utm_campaign_from_web', value);
      }
      if (key === 'utm_content') {
        this.localStorageService.set('utm_content_from_web', value);
      }
      if (key === 'keyword') {
        this.localStorageService.set('keyword_from_web', value);
      }
      if (key === 'seo_url') {
        this.localStorageService.set('ads_url_from_web', value);
      }
      if (key === 'rdt_id') {
        this.localStorageService.set('rdt_id_from_web', value);
      }
      // Agentic Monitoring ad links: LoginComponent sends these users to
      // /agentic-monitoring-login instead of the default login.
      if (key === 'agentic-monitoring-login') {
        this.localStorageService.set('agenticMonitoringLogin', true);
        // An Organic flag left from an earlier visit would otherwise win
        // on /login and show the Organic login.
        this.sessionService.clearOrganicEntryState();
      }
    });

    if (typeof this.localStorageService.get('darkMode') == 'boolean') {
      this.darkMode = this.localStorageService.get('darkMode') || false;
    } else {
      const deviceMode = matchMedia('(prefers-color-scheme: dark)');
      if (deviceMode.matches) {
        this.darkMode = true;
      }
    }

    const token: string = params.get('shopifyToken')!;

    // let isInIframe = false;
    // try {
    //   isInIframe = window.self !== window.top;
    // } catch (e) {
    //   isInIframe = true;
    // }

    // const isShopify = token || isInIframe;

    this.appState.isShopify.set(
      sessionStorage.getItem('shopifyUser') == 'true',
    );

    if (token) {
      sessionStorage.setItem('shopifyUser', 'true');

      this.appState.isShopify.set(true);
      this.showMainLoader = true;
      this.showShopifyError = false;
      this.userService.verifyShopifyUser(token).subscribe({
        next: (res: any) => {
          this.removeMainLoader();

          this.sessionService.login(res);
          this.router.navigate(['dashboard']);
        },
        error: () => {
          this.showShopifyErrorMessage();
        },
      });

      return;
    } else if (this.isShopify()) {
      this.removeMainLoader();
    }

    const shopifyShowError: string = params.get('shopifyShowError')!;
    if (shopifyShowError) {
      this.showShopifyErrorMessage();
    }
  }

  private setHtmlLang(lang: string) {
    document.documentElement.lang = lang;
  }

  private showShopifyErrorMessage(): void {
    this.removeMainLoader();
    this.showShopifyError = true;
    this.notificationService.error('Shopify verification failed');
    this.router.navigate(['/shopify-error']);
  }

  private bridgeGoogleAdsOAuthCallback() {
    if (!window.opener || window.opener.closed) {
      return;
    }

    // HubSpot's OAuth redirect also carries code/state, but in the same tab.
    if (window.location.pathname.startsWith('/hubspot')) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const state = params.get('state') || '';
    const code = params.get('code') || '';

    if (!state || !code) {
      return;
    }

    window.opener.postMessage(
      {
        type: 'GoogleAdsOAuthCallback',
        params: {
          state,
          iss: params.get('iss') || 'https://accounts.google.com',
          code,
          scope: params.get('scope') || '',
        },
      },
      '*',
    );

    window.close();
  }

  replaceOldLocalStorage() {
    try {
      const organicLogin = localStorage.getItem('organicLogin');
      if (organicLogin) {
        this.localStorageService.set('organicLogin', true);
      }
      localStorage.removeItem('organicLogin');
    } catch (e) {}
    try {
      const organicSignup = localStorage.getItem('organic_signup');
      if (organicSignup) {
        this.localStorageService.set('organic_signup', true);
      }
      localStorage.removeItem('organic_signup');
    } catch (e) {}
    try {
      const signup_organic_url = localStorage.getItem('signup_organic_url');
      if (signup_organic_url) {
        this.localStorageService.set('signup_organic_url', signup_organic_url);
      }
      localStorage.removeItem('signup_organic_url');
    } catch (e) {}
    try {
      const organic_signup_premium = localStorage.getItem(
        'organic_signup_premium',
      );
      if (organic_signup_premium) {
        this.localStorageService.set('organic_signup_premium', true);
      }
      localStorage.removeItem('organic_signup_premium');
    } catch (e) {}
    try {
      const is_7_days_free_trial = localStorage.getItem('is_7_days_free_trial');
      if (is_7_days_free_trial) {
        this.localStorageService.set('is_7_days_free_trial', true);
      }
      localStorage.removeItem('is_7_days_free_trial');
    } catch (e) {}
    try {
      const auth_key = localStorage.getItem('auth_key');
      if (auth_key) {
        this.localStorageService.set('auth_key', auth_key);
      }
      localStorage.removeItem('auth_key');
    } catch (e) {}
    try {
      const email = localStorage.getItem('email');
      if (email) {
        this.localStorageService.set('email', email);
      }
      localStorage.removeItem('email');
    } catch (e) {}
    try {
      const name = localStorage.getItem('name');
      if (name) {
        this.localStorageService.set('name', name);
      }
      localStorage.removeItem('name');
    } catch (e) {}
    try {
      const language = localStorage.getItem('language');
      if (language) {
        this.localStorageService.set('language', language);
      }
      localStorage.removeItem('language');
    } catch (e) {}
    try {
      const darkMode = localStorage.getItem('darkMode');
      if (darkMode) {
        this.localStorageService.set('darkMode', darkMode);
      }
      localStorage.removeItem('darkMode');
    } catch (e) {}
    try {
      const isWhiteLabel = localStorage.getItem('isWhiteLabel');
      if (isWhiteLabel) {
        this.localStorageService.set('isWhiteLabel', true);
      }
      localStorage.removeItem('isWhiteLabel');
    } catch (e) {}
    try {
      const bugSubscription = localStorage.getItem('bugSubscription');
      if (bugSubscription) {
        this.localStorageService.set('bugSubscription', bugSubscription);
      }
      localStorage.removeItem('bugSubscription');
    } catch (e) {}
    try {
      const seoSubscription = localStorage.getItem('seoSubscription');
      if (seoSubscription) {
        this.localStorageService.set('seoSubscription', seoSubscription);
      }
      localStorage.removeItem('seoSubscription');
    } catch (e) {}
    try {
      const seoSubscriptionObj = localStorage.getItem('seoSubscriptionObj');
      if (seoSubscriptionObj) {
        const parsed = JSON.parse(seoSubscriptionObj);
        this.localStorageService.set('seoSubscriptionObj', parsed);
      }
      localStorage.removeItem('seoSubscriptionObj');
    } catch (e) {}
    try {
      const seo_roles = localStorage.getItem('seo_roles');
      if (seo_roles) {
        const parsed = JSON.parse(seo_roles);
        this.localStorageService.set('seo_roles', parsed);
      }
      localStorage.removeItem('seo_roles');
    } catch (e) {}
    try {
      const kpiSymbol = localStorage.getItem('kpiSymbol');
      if (kpiSymbol) {
        this.localStorageService.set('kpiSymbol', kpiSymbol);
      }
      localStorage.removeItem('kpiSymbol');
    } catch (e) {}
    try {
      const allUserTargetPrivileges = localStorage.getItem(
        'allUserTargetPrivileges',
      );
      if (allUserTargetPrivileges) {
        const parsed = JSON.parse(allUserTargetPrivileges);
        this.localStorageService.set('allUserTargetPrivileges', parsed);
      }
      localStorage.removeItem('allUserTargetPrivileges');
    } catch (e) {}
    try {
      const user_ads_roles = localStorage.getItem('user_ads_roles');
      if (user_ads_roles) {
        const parsed = JSON.parse(user_ads_roles);
        this.localStorageService.set('user_ads_roles', parsed);
      }
      localStorage.removeItem('user_ads_roles');
    } catch (e) {}
  }

  private removeMainLoader(): void {
    this.showContent = true;
    setTimeout(() => {
      this.showMainLoader = false;
    }, 500);
  }
}
